import React, { useState, useEffect, useRef } from "react";
import {
  Table,
  Input,
  Select,
  Button,
  Card,
  Tag,
  Pagination,
  message,
  Tooltip,
} from "antd";
import {
  SearchOutlined,
  EyeOutlined,
  FilterOutlined,
  FileTextOutlined,
} from "@ant-design/icons";
import { getClientSearch } from "../api/client/clientRequest";
import { getPublicInstitutions } from "../api/public_institutions/publicInstitutionRequest";
import { CLIENT_TYPES, CLIENT_TYPES_COLORS } from "../utils/constants";
import ClientCreateModal from "../components/ClientCreateModal";
import { useSelector } from "react-redux";

const { Option } = Select;

const ClientsPage = () => {
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [filters, setFilters] = useState({
    type: undefined,
    cin: "",
    ice: "",
    rc: "",
    public_institution_id: undefined,
  });
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0,
  });
  const [sorting, setSorting] = useState({
    field: "created_at",
    order: "desc",
  });
  const [publicInstitutions, setPublicInstitutions] = useState([]);
  const [showFilters, setShowFilters] = useState(false);

  const user = useSelector((state) => state.user.user);
  const [isExportingExcel, setIsExportingExcel] = useState(false);

  const workerPdfRef = useRef(null);
  const workerExcelRef = useRef(null);

  useEffect(() => {
    workerPdfRef.current = new Worker(
      new URL("../workers/pdfWorker.js", import.meta.url)
    );
    workerExcelRef.current = new Worker(
      new URL("../workers/excelWorker.js", import.meta.url)
    );

    workerPdfRef.current.onmessage = (e) => {
      const { status, blobURL, operation, filename, message } = e.data;

      // Handle different status updates
      switch (status) {
        case "starting":
          console.log("PDF generation starting...");
          break;
        case "generating":
          console.log("Creating PDF document...");
          // You could update a more granular status message here if needed
          break;
        case "complete":
          console.log("PDF generation complete");

          if (operation === "download") {
            const link = document.createElement("a");
            link.href = blobURL;
            link.download = filename || "file.pdf";
            link.click();
            URL.revokeObjectURL(blobURL);
          } else if (operation === "open") {
            window.open(blobURL, "_blank");
          }

          break;
        case "error":
          console.error("PDF generation error:", message);
          // message.error(`Erreur lors de la génération du PDF: ${message}`);
          break;
        default:
          console.warn("Unknown status:", status);
          break;
      }
    };

    workerExcelRef.current.onmessage = (e) => {
      const { success, blobURL, filename, error } = e.data;

      if (success) {
        const link = document.createElement("a");
        link.href = blobURL;
        link.download = filename || "file.xlsx";
        link.click();
        URL.revokeObjectURL(blobURL);
      } else {
        message.error(
          `Erreur lors de la génération du fichier Excel: ${error}`
        );
      }
    };

    return () => {
      workerPdfRef.current?.terminate();
      workerExcelRef.current?.terminate();
    };
  }, []);

  // Récupérer les données des clients
  const fetchClients = async () => {
    setLoading(true);
    try {
      const params = {
        page: pagination.current,
        per_page: pagination.pageSize,
        sort_by: sorting.field,
        sort_direction: sorting.order,
        search: searchTerm || undefined,
        ...Object.fromEntries(
          Object.entries(filters).filter(
            ([_, value]) => value !== undefined && value !== ""
          )
        ),
      };

      const response = await getClientSearch(params);
      setClients(response.data);
      setPagination({
        ...pagination,
        total: response.total,
      });
    } catch (error) {
      message.error("Erreur lors du chargement des clients");
      console.error("Erreur:", error);
    } finally {
      setLoading(false);
    }
  };

  // Récupérer les institutions publiques
  const fetchPublicInstitutions = async () => {
    try {
      const response = await getPublicInstitutions();
      setPublicInstitutions(response);
    } catch (error) {
      message.error("Erreur lors du chargement des Organisme publics");
      console.error("Erreur:", error);
    }
  };

  useEffect(() => {
    fetchClients();
  }, [pagination.current, pagination.pageSize, sorting]);

  // Effet séparé pour les filtres et la recherche avec debounce
  useEffect(() => {
    const timer = setTimeout(() => {
      fetchClients();
    }, 500);

    return () => clearTimeout(timer);
  }, [searchTerm, filters]);

  useEffect(() => {
    fetchPublicInstitutions();
  }, []);

  const handleSearch = (e) => {
    setSearchTerm(e.target.value);
    setPagination({ ...pagination, current: 1 });
  };

  const handleFilterChange = (key, value) => {
    const updatedFilters = { ...filters };

    if (key === "type") {
      updatedFilters.cin = "";
      updatedFilters.ice = "";
      updatedFilters.rc = ""; // Add rc reset on type change
      updatedFilters.public_institution_id = undefined;
    }

    setFilters({ ...updatedFilters, [key]: value });
    setPagination({ ...pagination, current: 1 });
  };

  const handleReset = () => {
    setFilters({
      type: undefined,
      cin: "",
      ice: "",
      rc: "",
      public_institution_id: undefined,
    });
    setSearchTerm("");
    setPagination({ ...pagination, current: 1 });
    setSorting({
      field: "created_at",
      order: "desc",
    });
  };
  const handleTableChange = (pagination, _, sorter) => {
    setPagination((prevPagination) => ({
      ...prevPagination,
      current: pagination.current || 1, // Ensure current page is valid
      pageSize: pagination.pageSize || 10, // Ensure pageSize is valid
    }));

    if (sorter && sorter.field) {
      setSorting({
        field: sorter.field,
        order: sorter.order === "ascend" ? "asc" : "desc",
      });
    } else {
      setSorting({
        field: "created_at",
        order: "desc",
      });
    }
  };

  const handleViewDetails = (clientId) => {
    // Navigation vers la page de détails
    window.open(`/client/${clientId}`);
  };

  const getTableName = (record) => {
    if (!record) return "-";
    if (record.type === "individuals") {
      return (
        `${record.first_name || ""} ${record.last_name || ""}`.trim() || "-"
      );
    }
    return record.name || "-";
  };

  const handleExcelClick = async () => {
    setIsExportingExcel(true);
    try {
      const timestamp = new Date().toISOString().replace(/:/g, "-");
      const filename = `Clients_${timestamp}.xlsx`;
      const baseUrl = window.location.origin;

      const params = {
        no_pagination: true,
        sort_by: sorting.field,
        sort_direction: sorting.order,
        search: searchTerm || undefined,
        ...Object.fromEntries(
          Object.entries(filters).filter(
            ([_, value]) => value !== undefined && value !== ""
          )
        ),
      };

      const dataSource = await getClientSearch(params);

      // Prepare only the necessary data to reduce processing overhead
      const formattedData = dataSource.map((it) => ({
        name: getTableName(it),
        email: it.email || "-",
        phone: it.phone || "-",
        cin: it.type === "individuals" ? it.cin || "-" : "-",
        ice: it.type === "companies" ? it.ice || "-" : "-",
        rc: it.type === "companies" ? it.rc || "-" : "-",
        public_institution:
          it.type === "institutions" ? it.public_institution?.name || "-" : "-",
        type: CLIENT_TYPES[it.type] || "-",
      }));

      const data = [
        ["Clients", ``, ``, ``],
        [],
        [],
        user.full_name,
        [],
        [
          [
            {
              text: "Nom / Raison Sociale",
              type: "string",
              key: "name",
            },
            {
              text: "Email",
              type: "string",
              key: "email",
            },
            {
              text: "Téléphone",
              type: "string",
              key: "phone",
            },
            {
              text: "CIN",
              type: "string",
              key: "cin",
            },
            {
              text: "ICE",
              type: "string",
              key: "ice",
            },
            {
              text: "RC",
              type: "string",
              key: "rc",
            },

            {
              text: "Organisme public",
              type: "string",
              key: "public_institution",
            },
            { text: "Type", type: "string", key: "type" },
          ],
        ],
        [formattedData],
        baseUrl,
      ];

      // Post message to worker
      workerExcelRef.current.postMessage({ data, filename });
    } catch (error) {
      message.error("Erreur lors de la génération du fichier Excel");
    } finally {
      setIsExportingExcel(false);
    }
  };

  const columns = [
    {
      title: "Nom / Raison Sociale",
      dataIndex: "name",
      key: "name",
      sorter: true,
      sortOrder:
        sorting.field === "name"
          ? sorting.order === "asc"
            ? "ascend"
            : "descend"
          : null,
      render: (_, record) => getTableName(record),
    },
    {
      title: "Email",
      dataIndex: "email",
      key: "email",
      sorter: true,
      sortOrder:
        sorting.field === "email"
          ? sorting.order === "asc"
            ? "ascend"
            : "descend"
          : null,
      render: (email) => email || "-",
    },
    {
      title: "Téléphone",
      dataIndex: "phone",
      key: "phone",
      render: (phone) => phone || "-",
    },
    {
      title: "CIN",
      dataIndex: "cin",
      key: "cin",
      render: (cin, record) =>
        record.type === "individuals" ? cin || "-" : "-",
    },
    {
      title: "ICE",
      dataIndex: "ice",
      key: "ice",
      render: (ice, record) => (record.type === "companies" ? ice || "-" : "-"),
    },
    {
      title: "RC",
      dataIndex: "rc",
      key: "rc",
      render: (rc, record) => (record.type === "companies" ? rc || "-" : "-"),
    },
    {
      title: "Organisme public",
      dataIndex: ["public_institution", "name"],
      key: "public_institution",
      render: (public_institution, record) =>
        record.type === "institutions" ? public_institution || "-" : "-",
    },
    {
      title: "Type",
      dataIndex: "type",
      key: "type",
      sorter: true,
      sortOrder:
        sorting.field === "type"
          ? sorting.order === "asc"
            ? "ascend"
            : "descend"
          : null,
      render: (type) => {
        if (!type) return "-";
        let color = CLIENT_TYPES_COLORS[type] || "default";
        let text = CLIENT_TYPES[type] || type;
        return <Tag color={color}>{text}</Tag>;
      },
    },
    {
      title: "Actions",
      key: "actions",
      render: (_, record) => (
        <Tooltip title="Voir les détails">
          <Button
            type="primary"
            icon={<EyeOutlined />}
            size="small"
            onClick={() => handleViewDetails(record.id)}
          >
            Détails
          </Button>
        </Tooltip>
      ),
    },
  ];

  return (
    <div className="p-4 md:p-6">
      <Card title="Liste des Clients" className="shadow-md">
        <div className="mb-6 flex flex-col md:flex-row gap-4 items-start md:items-end">
          <div className="flex-1 w-full md:w-auto">
            <Input
              placeholder="Rechercher (Nom, Email, Tél...)"
              prefix={<SearchOutlined />}
              value={searchTerm}
              onChange={handleSearch}
              allowClear
              className="w-full"
              size="middle"
            />
          </div>
          <div className="flex gap-2 flex-wrap">
            <Button
              icon={<FilterOutlined />}
              onClick={() => setShowFilters(!showFilters)}
              type={showFilters ? "primary" : "default"}
            >
              Filtres
            </Button>
            {showFilters && (
              <Button onClick={handleReset}>Réinitialiser</Button>
            )}
          </div>
        </div>

        {showFilters && (
          <div className="mb-6 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 p-4 border rounded bg-gray-50">
            <div>
              <div className="text-sm text-gray-600 mb-1 font-medium">
                Type de client
              </div>
              <Select
                placeholder="Sélectionner un type"
                allowClear
                className="w-full"
                onChange={(value) => handleFilterChange("type", value)}
                value={filters.type}
              >
                {Object.keys(CLIENT_TYPES).map((type) => (
                  <Option key={type} value={type}>
                    {CLIENT_TYPES[type]}
                  </Option>
                ))}
              </Select>
            </div>

            {(filters.type === "institutions" ||
              filters.type === undefined) && (
              <div>
                <div className="text-sm text-gray-600 mb-1 font-medium">
                  Organisme public
                </div>
                <Select
                  placeholder="Sélectionner un Organisme"
                  allowClear
                  showSearch
                  filterOption={(input, option) =>
                    option.children.toLowerCase().includes(input.toLowerCase())
                  }
                  className="w-full"
                  onChange={(value) =>
                    handleFilterChange("public_institution_id", value)
                  }
                  value={filters.public_institution_id}
                  loading={
                    !publicInstitutions.length &&
                    (filters.type === "institutions" ||
                      filters.type === undefined)
                  }
                >
                  {publicInstitutions.map((institution) => (
                    <Option key={institution.id} value={institution.id}>
                      {institution.name}
                    </Option>
                  ))}
                </Select>
              </div>
            )}

            {(filters.type === "individuals" || filters.type === undefined) && (
              <div>
                <div className="text-sm text-gray-600 mb-1 font-medium">
                  CIN
                </div>
                <Input
                  placeholder="Numéro CIN"
                  allowClear
                  value={filters.cin}
                  onChange={(e) => handleFilterChange("cin", e.target.value)}
                />
              </div>
            )}

            {(filters.type === "companies" || filters.type === undefined) && (
              <div>
                <div className="text-sm text-gray-600 mb-1 font-medium">
                  ICE
                </div>
                <Input
                  placeholder="Numéro ICE"
                  allowClear
                  value={filters.ice}
                  onChange={(e) => handleFilterChange("ice", e.target.value)}
                />
              </div>
            )}

            {(filters.type === "companies" || filters.type === undefined) && (
              <div>
                <div className="text-sm text-gray-600 mb-1 font-medium">RC</div>
                <Input
                  placeholder="Numéro RC"
                  allowClear
                  value={filters.rc}
                  onChange={(e) => handleFilterChange("rc", e.target.value)}
                />
              </div>
            )}
          </div>
        )}

        <div className="flex mb-4 justify-end">
          <ClientCreateModal
            onClientCreated={fetchClients}
            publicInstitutions={publicInstitutions}
          />
        </div>

        <div className="overflow-x-auto">
          <div style={{ marginBottom: 16 }} className="flex justify-end">
            <Button
              icon={<FileTextOutlined />}
              onClick={async () => await handleExcelClick()}
              className="hover:border-green-700 hover:text-green-500 mr-4"
              disabled={isExportingExcel}
            >
              Exporter Excel
            </Button>
            {/* <Dropdown
                menu={{ items: getPdfMenuItems(handlePdfClick, pdfExporting) }}
                disabled={pdfExporting}
                className="hover:border-red-700 hover:text-red-500"
              >
                <Button icon={<FileTextOutlined />} disabled={pdfExporting}>
                  Export PDF{" "}
                  {pdfExporting ? (
                    "..."
                  ) : (
                    <span style={{ marginLeft: 5 }}>▼</span>
                  )}
                </Button>
              </Dropdown> */}
          </div>
          <Table
            columns={columns}
            dataSource={clients}
            rowKey="id"
            loading={loading}
            pagination={false}
            onChange={handleTableChange}
            className="mb-4"
            locale={{
              emptyText: "Aucun client trouvé",
              triggerDesc: "Trier par ordre décroissant",
              triggerAsc: "Trier par ordre croissant",
              cancelSort: "Annuler le tri",
            }}
          />
        </div>

        <div className="flex justify-center md:justify-end mt-4">
          <Pagination
            current={pagination.current}
            pageSize={pagination.pageSize}
            total={pagination.total}
            onChange={(page, pageSize) => {
              const currentPage = page > 0 ? page : 1;
              const currentPageSize = pageSize > 0 ? pageSize : 10;
              setPagination({
                ...pagination,
                current: currentPage,
                pageSize: currentPageSize,
              });
            }}
            showSizeChanger
            pageSizeOptions={["10", "20", "50", "100"]}
            showTotal={(total, range) =>
              `${range[0]}-${range[1]} sur ${total} clients`
            }
            locale={{
              items_per_page: "/ page",
            }}
          />
        </div>
      </Card>
    </div>
  );
};

export default ClientsPage;
